// src/store/slices/admin/regionSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

export const getRegions = createAsyncThunk(
    'regions/getRegions',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.get(API_ENDPOINTS.REGIONS, { params });
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Regions retrieved', data };
            } else {
                return rejectWithValue({ message: message || 'Failed to fetch regions', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch regions';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const getRegion = createAsyncThunk(
    'regions/getRegion',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.get(API_ENDPOINTS.REGION(id));
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Region retrieved', region: data };
            } else {
                return rejectWithValue({ message: message || 'Failed to fetch region', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch region';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const createRegion = createAsyncThunk(
    'regions/createRegion',
    async (data, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.post(API_ENDPOINTS.REGIONS + '/create', data);
            const { status, message, data: responseData } = response.data;
            if (status === 201 || status === 200) {
                return { success: true, message: message || 'Region created', region: responseData };
            } else {
                return rejectWithValue({ message: message || 'Failed to create region', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to create region';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const updateRegion = createAsyncThunk(
    'regions/updateRegion',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.put(API_ENDPOINTS.REGION(id), data);
            const { status, message, data: responseData } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Region updated', region: responseData };
            } else {
                return rejectWithValue({ message: message || 'Failed to update region', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to update region';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const deleteRegion = createAsyncThunk(
    'regions/deleteRegion',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.delete(API_ENDPOINTS.REGION(id));
            const { status, message } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Region deleted', id };
            } else {
                return rejectWithValue({ message: message || 'Failed to delete region', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to delete region';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const toggleRegionStatus = createAsyncThunk(
    'regions/toggleStatus',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.patch(API_ENDPOINTS.REGION_TOGGLE(id));
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Status toggled', id, is_active: data?.is_active };
            } else {
                return rejectWithValue({ message: message || 'Failed to toggle status', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to toggle status';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const exportRegions = createAsyncThunk(
    'regions/export',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.get(API_ENDPOINTS.REGIONS_EXPORT, { params });
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Exported successfully', data };
            } else {
                return rejectWithValue({ message: message || 'Failed to export', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to export';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

// =============================================
// SLICE
// =============================================

const initialState = {
    items: [],
    pagination: { current_page: 1, last_page: 1, per_page: 15, total: 0 },
    currentItem: null,
    isLoading: false,
    isSubmitting: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

const regionSlice = createSlice({
    name: 'regions',
    initialState,
    reducers: {
        clearError: (state) => { state.error = null; },
        clearSuccessMessage: (state) => { state.successMessage = null; },
        clearApiMessage: (state) => { state.apiMessage = null; },
        clearState: (state) => {
            state.items = [];
            state.pagination = { current_page: 1, last_page: 1, per_page: 15, total: 0 };
            state.currentItem = null;
            state.isLoading = false;
            state.isSubmitting = false;
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
        },
        clearCurrentItem: (state) => { state.currentItem = null; },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getRegions.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getRegions.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload;
                const responseData = payload.data || {};
                state.items = responseData.data || [];
                state.pagination = responseData.meta || { current_page: 1, last_page: 1, per_page: 15, total: 0 };
                state.successMessage = payload.message || 'Regions retrieved';
                state.apiMessage = payload.message || 'Regions retrieved';
                state.error = null;
            })
            .addCase(getRegions.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch regions';
                state.apiMessage = action.payload?.message || 'Failed to fetch regions';
                state.successMessage = null;
                state.items = [];
                state.pagination = { current_page: 1, last_page: 1, per_page: 15, total: 0 };
            })
            .addCase(getRegion.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getRegion.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentItem = action.payload.region || null;
                state.successMessage = action.payload.message || 'Region retrieved';
                state.apiMessage = action.payload.message || 'Region retrieved';
                state.error = null;
            })
            .addCase(getRegion.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch region';
                state.apiMessage = action.payload?.message || 'Failed to fetch region';
                state.successMessage = null;
                state.currentItem = null;
            })
            .addCase(createRegion.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(createRegion.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const newItem = action.payload.region;
                if (newItem) {
                    state.items = [newItem, ...state.items];
                }
                state.successMessage = action.payload.message || 'Region created';
                state.apiMessage = action.payload.message || 'Region created';
                state.error = null;
            })
            .addCase(createRegion.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to create region';
                state.apiMessage = action.payload?.message || 'Failed to create region';
                state.successMessage = null;
            })
            .addCase(updateRegion.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateRegion.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const updated = action.payload.region;
                if (updated) {
                    state.items = state.items.map(item => item.id === updated.id ? updated : item);
                    if (state.currentItem?.id === updated.id) {
                        state.currentItem = updated;
                    }
                }
                state.successMessage = action.payload.message || 'Region updated';
                state.apiMessage = action.payload.message || 'Region updated';
                state.error = null;
            })
            .addCase(updateRegion.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update region';
                state.apiMessage = action.payload?.message || 'Failed to update region';
                state.successMessage = null;
            })
            .addCase(deleteRegion.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deleteRegion.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const id = action.payload.id;
                state.items = state.items.filter(item => item.id !== id);
                if (state.currentItem?.id === id) state.currentItem = null;
                state.successMessage = action.payload.message || 'Region deleted';
                state.apiMessage = action.payload.message || 'Region deleted';
                state.error = null;
            })
            .addCase(deleteRegion.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete region';
                state.apiMessage = action.payload?.message || 'Failed to delete region';
                state.successMessage = null;
            })
            .addCase(toggleRegionStatus.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(toggleRegionStatus.fulfilled, (state, action) => {
                state.isLoading = false;
                const { id, is_active } = action.payload;
                state.items = state.items.map(item => item.id === id ? { ...item, is_active } : item);
                if (state.currentItem?.id === id) state.currentItem = { ...state.currentItem, is_active };
                state.successMessage = action.payload.message || 'Status toggled';
                state.apiMessage = action.payload.message || 'Status toggled';
                state.error = null;
            })
            .addCase(toggleRegionStatus.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to toggle status';
                state.apiMessage = action.payload?.message || 'Failed to toggle status';
                state.successMessage = null;
            })
            .addCase(exportRegions.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(exportRegions.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message || 'Exported successfully';
                state.apiMessage = action.payload.message || 'Exported successfully';
                state.error = null;
            })
            .addCase(exportRegions.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to export';
                state.apiMessage = action.payload?.message || 'Failed to export';
                state.successMessage = null;
            });
    }
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearState,
    clearCurrentItem,
} = regionSlice.actions;

export const selectRegions = (state) => state.regions?.items || [];
export const selectRegionPagination = (state) => state.regions?.pagination || { current_page: 1, last_page: 1, per_page: 15, total: 0 };
export const selectCurrentRegion = (state) => state.regions?.currentItem || null;
export const selectRegionsLoading = (state) => state.regions?.isLoading || false;
export const selectRegionsSubmitting = (state) => state.regions?.isSubmitting || false;
export const selectRegionsError = (state) => state.regions?.error || null;
export const selectRegionsApiMessage = (state) => state.regions?.apiMessage || null;

export default regionSlice.reducer;